import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import SECTIONS from "modules/patient/sections";
import { RAIL_OPEN, RAIL_CLOSE } from "modules/patient/sections/railEvents";
import { ANTH_V2_SELECT_PARAM } from "modules/patient/sections/anthropometric/events";
import { PARAMETER_BY_KEY } from "modules/patient/sections/anthropometric/data";
import HealthOverviewCard from "modules/patient/components/HealthOverviewCard";
import PatientDetailsDialog from "modules/patient/components/PatientDetailsDialog";
import CallNoteModal from "modules/patient/components/CallNoteModal";
import { callNoteVariant } from "modules/patient/helper/callNoteVariant";
import { STATUS_LABEL } from "modules/appointments/helper/appointmentConstants";
import AnthroOverviewCard from "modules/patient/components/AnthroOverviewCard";

import MOBILE from "../mobileConfig";
import useCssVarHeight from "../useCssVarHeight";
import useVisualViewport from "../useVisualViewport";
import useDragScroll from "../useDragScroll";
import { useScrollLock } from "../scrollLock";
import MobileTopBar from "../components/MobileTopBar";
import MobileApptStrip from "../components/MobileApptStrip";
import MobileApptSheet from "../components/MobileApptSheet";
import MobilePatientMeta from "../components/MobilePatientMeta";
import MobileTabRail from "../components/MobileTabRail";
import MobileActionBar from "../components/MobileActionBar";
import DocumentsMenu from "modules/patient/components/DocumentsMenu";
import BottomSheet from "../components/BottomSheet";
import "../styles/mobile.scss";

/** Figma 5330:2 — the handset on the accent Call patient button. */
const PhoneIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M5 1.9 6.2 4.5 5 5.9a8.3 8.3 0 0 0 4.3 4.3l1.4-1.2 2.6 1.2v2.7a.9.9 0 0 1-1 .9A12.2 12.2 0 0 1 2.2 3.9a.9.9 0 0 1 .9-1H5Z" />
  </svg>
);

const TAB_KEYS = MOBILE.TABS.map((t) => t.key);
const labelFor = (key) => {
  const tab = MOBILE.TABS.find((t) => t.key === key);
  return tab ? tab.label : "";
};

/**
 * Which tab a `?section=` deep link should open.
 *
 * Checked against the MOBILE tab list rather than the full SECTIONS registry,
 * and that difference is the point: Create Rx's "+ Add diagnosis" links to
 * `?section=diagnosis`, which mobile has, but a link to `drug-schedule` names a
 * section mobile does not show. Desktop opens it; here it falls back to
 * Overview rather than selecting a tab that is not on the rail.
 */
const tabFromUrl = (search) => {
  try {
    const want = new URLSearchParams(search || "").get("section");
    return want && TAB_KEYS.indexOf(want) !== -1 ? want : null;
  } catch (e) {
    return null;
  }
};

/**
 * The patient record on a phone.
 *
 * Renders INSTEAD of PatientDetails' desktop tree, from the values that screen
 * has already computed — every prop here is state or a callback that exists
 * whether or not this file does. Nothing is fetched, saved, or decided here; it
 * is a second arrangement of the same parts.
 *
 * The desktop shell is nav | content | rail. This is:
 *
 *   row 1  top bar            back · name · call · ⋮
 *   row 2  appointment strip  when + status, taps through to the detail sheet
 *   row 3  patient meta       ids, therapy, languages
 *   row 4  tab rail           Overview + the eight sections that feed Create Rx
 *   row 5  content            the active tab, over a pinned action bar
 *
 * ...and the rail becomes a bottom sheet. See the note on `dismissSheet` for
 * how, which is the one genuinely subtle thing in this file.
 */
const MobilePatientScreen = ({
  loading,
  error,
  ready,
  patient,
  appointment,
  appointmentId,
  languages,
  languagesState,
  onLanguagesSaved,
  condition,
  therapyTag,
  onTherapyChanged,
  onPatientUpdated,
  reasonName,
  pastAppointments,
  upcomingAppointments,
  touchpointsState,
  onOpenAppointment,
  onOpenAppointmentDetails,
  callNoteForStatus,
  onCloseCallNote,
  stuck,
  originalBookingName,
  section,
  onSelectSection,
  updating,
  onChangeStatus,
  onReschedule,
  rescheduleDisabledReason,
  onUpdateBookingFor,
  bookingForBlockedReason,
  canPrescribe,
  onCreatePrescription,
  onCreateHealthPlan,
  onBack,
  call,
  rescheduleUI,
  dialogs,
  locationSearch,
  autoOpenAdd,
  onAutoOpenAdd,
}) => {
  const [activeTab, setActiveTab] = useState(() => tabFromUrl(locationSearch) || "overview");
  const [apptSheetOpen, setApptSheetOpen] = useState(false);
  /* Both dialogs are owned HERE rather than by the rows that open them: the
     pencil is in row 1 and the call note is in the appointment sheet, but
     either can be opened from more than one place and neither belongs to the
     row that happens to carry its button. Desktop keeps both in
     PatientHeaderCard for the same reason. */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [callNoteOpen, setCallNoteOpen] = useState(false);

  /* Is a section editor asking to be seen? The editors say so with the events
     they already emit for the desktop rail — this is one more listener on an
     existing broadcast, not a rewiring of it. PatientShell.js:44-56 subscribes
     to exactly the same pair. */
  const [railOpen, setRailOpen] = useState(false);

  /**
   * Bumping this REMOUNTS the active section's editor, and that is how the
   * sheet closes.
   *
   * useSheetStack owns the open sheets and exposes closeAll(), but only to the
   * editor that called it — there is no way to reach in from outside, and
   * adding one would mean editing shared code that desktop also runs. A remount
   * gets there by a door that is already open: unmounting drops every sheet AND
   * fires RAIL_CLOSE from the hook's own cleanup (useSheetStack.js:192), and
   * mounting re-attaches the *_ADD listener so the next tap reopens it.
   *
   * The cost is that a half-typed form is discarded, which is what dismissing a
   * sheet means anyway. The forms' own Save and Cancel are untouched and remain
   * the way to close one without losing work.
   */
  /* Which anthropometric parameter the sheet is editing, so its title can say
     "Weight" rather than the tab's name. Read off the SAME event the editor
     listens to (AnthropometricEditor.js:665-680) — one more listener on an
     existing broadcast, and the only section that opens per-row rather than
     from a single Add. Only ever one at a time here: opening a second means
     tapping a row behind the sheet, and the backdrop covers them. */
  const [anthroParam, setAnthroParam] = useState(null);

  /* "Add complaint" or "Update complaint" rather than the tab's own name —
     whichever of the section's two channels opened the sheet. See
     MOBILE.SHEET_TITLES. */
  const [sheetAction, setSheetAction] = useState(null);

  const [editorKey, setEditorKey] = useState(0);
  const dismissSheet = useCallback(() => {
    setAnthroParam(null);
    setSheetAction(null);
    setEditorKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const onOpen = () => setRailOpen(true);
    const onClose = () => setRailOpen(false);
    const onParam = (key) => setAnthroParam(key);
    EventService.addListener(RAIL_OPEN, onOpen);
    EventService.addListener(RAIL_CLOSE, onClose);
    EventService.addListener(ANTH_V2_SELECT_PARAM, onParam);

    /* One pair of listeners per configured section, on the channels the
       section's own table already emits — nothing new is broadcast. */
    const titled = Object.keys(MOBILE.SHEET_TITLES).map((key) => {
      const cfg = MOBILE.SHEET_TITLES[key];
      const onAdd = () => setSheetAction({ key, mode: "add" });
      const onEdit = () => setSheetAction({ key, mode: "edit" });
      EventService.addListener(cfg.addEvent, onAdd);
      EventService.addListener(cfg.editEvent, onEdit);
      return () => {
        EventService.removeListener(cfg.addEvent, onAdd);
        EventService.removeListener(cfg.editEvent, onEdit);
      };
    });

    return () => {
      EventService.removeListener(RAIL_OPEN, onOpen);
      EventService.removeListener(RAIL_CLOSE, onClose);
      EventService.removeListener(ANTH_V2_SELECT_PARAM, onParam);
      titled.forEach((off) => off());
    };
  }, []);

  /* Changing tab closes whatever the last tab had open, the same reset
     PatientShell.js:45 does when its active section changes. */
  useEffect(() => {
    setRailOpen(false);
    setAnthroParam(null);
    setSheetAction(null);
  }, [activeTab]);

  /**
   * Cross-section links (the overview card's lab "View all", the medicines
   * drug-allergy bar) move the PARENT's section, so follow it here.
   *
   * Skipped on the first run: the parent's section starts at "anthropometric"
   * whether or not anyone asked for it, and following that would override the
   * Overview default this screen opens on.
   */
  const followedOnce = useRef(false);
  useEffect(() => {
    if (!followedOnce.current) {
      followedOnce.current = true;
      return;
    }
    if (TAB_KEYS.indexOf(section) !== -1) setActiveTab(section);
  }, [section]);

  const selectTab = useCallback(
    (key) => {
      setActiveTab(key);
      /* Keep the parent in step so the desktop tree is already on the right
         section if the window is widened mid-session. Overview is this screen's
         own idea and has no section to select. */
      /* Only what the desktop tree has a section for: Overview is this
         screen's own idea, and so is Documents — telling the desktop to select
         either would leave it on a section it cannot render. */
      if (key !== "overview" && SECTIONS[key]) onSelectSection(key);
    },
    [onSelectSection]
  );

  /* Keeps an open keyboard from burying the bottom of a sheet. */
  useVisualViewport();
  /* Mouse drag pans, for driving this layout in a narrow desktop window. */
  useDragScroll();
  /* Patient details is desktop's dialog and locks nothing; <html> is what
     scrolls here, so without this a drag on it moved the record behind. The
     same condition the dialog renders on, below. */
  useScrollLock(MOBILE.patientDetailsScrollLock && ready && detailsOpen);

  /* Rows 1, 2 and 4 stay put while the list scrolls under them, so each one's
     LIVE height has to feed the next one's offset — a long patient name wraps
     row 1, and the status pill can wrap row 2. Hardcoding either would tuck a
     row under its neighbour on some phones and leave a gap on others. Same
     approach, and the same ResizeObserver feature test, as --ph-sticky-h on the
     desktop screen (PatientDetails.js:548-563). */
  const row1Ref = useCssVarHeight("--zym-row1-h", [patient && patient.id, ready]);
  const row2Ref = useCssVarHeight("--zym-row2-h", [appointment && appointment.id, ready]);

  /* The Documents tab is this screen's own — no patient SECTION answers for
     it, so it gets its own pane and its own bar button. */
  const docsTab = MOBILE.documentsTab && activeTab === "documents";
  /** Set by the bar's "+ Upload"; the panel opens the modal and hands it back. */
  const [docsUpload, setDocsUpload] = useState(false);

  const entry = activeTab === "overview" || docsTab ? null : SECTIONS[activeTab];
  const Content = entry && entry.Content;
  const Editor = entry && entry.Editor;

  /* The most specific name available: the anthropometric parameter, else the
     action that opened the sheet, else the tab. */
  const anthroMeta =
    activeTab === "anthropometric" && anthroParam ? PARAMETER_BY_KEY[anthroParam] : null;
  const titleCfg =
    sheetAction && sheetAction.key === activeTab ? MOBILE.SHEET_TITLES[activeTab] : null;
  const sheetTitle =
    (anthroMeta && anthroMeta.label) ||
    (titleCfg && titleCfg[sheetAction.mode]) ||
    labelFor(activeTab);

  /* The pinned add button's event. BAR_EVENT_FOR_TAB wins where a tab's button
     should do something other than its add event — today only Lab, whose button
     opens the Digitise report modal; see that map for why it is a second one. */
  const barEvent = MOBILE.labAddOpensDigitise && MOBILE.BAR_EVENT_FOR_TAB[activeTab];
  const addEvent = barEvent || MOBILE.ADD_EVENT_FOR_TAB[activeTab];
  const addLabel = MOBILE.ADD_LABEL_FOR_TAB[activeTab];

  return (
    <div className={`zym-page${stuck ? " is-stuck" : ""}`}>
      <div className="zym-head" ref={row1Ref}>
        <MobileTopBar
          title={
            patient
              ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim() || "Patient"
              : "Patient"
          }
          onBack={onBack}
          onEditPatient={ready ? () => setDetailsOpen(true) : undefined}
          onOpenAddressBook={ready && call ? call.openAddressBook : undefined}
          menu={MOBILE.patientOverflowMenu}
        />
      </div>

      {ready && (
        <>
          {/* Wrapped so the sticky box and the measured box are the same
              element, and so the strip keeps its own borders inside it. */}
          <div className="zym-appt-wrap" ref={row2Ref}>
            <MobileApptStrip
              appointment={appointment}
              updating={updating}
              onChangeStatus={onChangeStatus}
              onOpenDetails={() => setApptSheetOpen(true)}
              /* Row 2 carries the reason and the documents button too; the
                 sheet keeps both. Its height is published by the ResizeObserver
                 in useCssVarHeight, so the rows below follow it growing. */
              reasonName={reasonName}
            />
          </div>

          <MobilePatientMeta
            patient={patient}
            condition={condition}
            therapyTag={therapyTag}
            onTherapyChanged={onTherapyChanged}
            languages={languages}
            languagesState={languagesState}
            onLanguagesSaved={onLanguagesSaved}
            originalBookingName={originalBookingName}
          />

          <MobileTabRail tabs={MOBILE.TABS} activeKey={activeTab} onSelect={selectTab} />
        </>
      )}

      <div className="zym-body">
        {loading && <div className="zym-message">Loading…</div>}

        {!loading && error && (
          <div className="zym-message is-error">
            <p>{error}</p>
            <button type="button" className="zym-btn is-primary" onClick={onBack}>
              Back to appointments
            </button>
          </div>
        )}

        {ready && activeTab === "overview" && (
          <div className="zym-overview">
            {/* Keyed by patient, for the same reason the desktop row is: on an
                in-place appointment switch the risk card must remount rather
                than paint patient A's risk for a frame under patient B's name.
                Which blocks of these two cards mobile shows is decided in
                mobile.scss, not here — no markup of theirs is touched. */}
            <HealthOverviewCard key={patient.id} patient={patient} />
            <AnthroOverviewCard patient={patient} />
          </div>
        )}

        {ready && docsTab && (
          /* The appointment sheet's "All documents", as a panel: the same
             component, the same fetch, the same folder grid and file list.
             Upload is the bar below, not a header icon (board). */
          <div className="zym-section zym-docs">
            <DocumentsMenu
              patientId={patient.id}
              variant="inline"
              uploadRequested={docsUpload}
              onUploadHandled={() => setDocsUpload(false)}
            />
          </div>
        )}

        {ready && activeTab !== "overview" && !docsTab && (
          <div className="zym-section">
            {Content ? (
              <Content
                patient={patient}
                appointmentId={appointmentId}
                /* `?section=<key>&add=1`: the section's add form, once — the
                   same two props PatientShell hands the desktop section. */
                autoOpenAdd={!!autoOpenAdd && autoOpenAdd === activeTab}
                onAutoOpenAdd={onAutoOpenAdd}
              />
            ) : (
              <div className="zym-message">This section isn’t available here yet.</div>
            )}
          </div>
        )}
      </div>

      {ready && activeTab === "overview" && (
        <MobileActionBar>
          {/* Create Rx leads, per the board (5330:2) — it is what this screen
              is for, and the call is the fallback when the record is not ready
              to prescribe from. One or the other, never both: a doctor
              prescribes, every other clinician category writes a health plan.
              The parent decides which handler exists; this draws the one it
              was given. */}
          {canPrescribe ? (
            <button type="button" className="zym-btn is-primary" onClick={onCreatePrescription}>
              Create Rx
            </button>
          ) : (
            <button type="button" className="zym-btn is-primary" onClick={onCreateHealthPlan}>
              Create health plan
            </button>
          )}
          <button
            type="button"
            className="zym-btn is-accent"
            disabled={!call.canCallPrimary}
            onClick={call.callPrimary}
            title={call.callPrimaryTitle}
          >
            <PhoneIcon />
            Call patient
          </button>
        </MobileActionBar>
      )}

      {ready && docsTab && (
        <MobileActionBar>
          <button
            type="button"
            className="zym-btn is-primary is-wide"
            onClick={() => setDocsUpload(true)}
          >
            + Upload
          </button>
        </MobileActionBar>
      )}

      {ready && activeTab !== "overview" && !docsTab && addEvent && (
        <MobileActionBar>
          <button
            type="button"
            className="zym-btn is-primary is-wide"
            onClick={() => EventService.emit(addEvent)}
          >
            {addLabel || "Add"}
          </button>
        </MobileActionBar>
      )}

      {/* The editor stays mounted for as long as its tab is selected, open or
          shut — it is what listens for the add event that reopens it. The sheet
          hides it; it does not unmount it. */}
      {ready && Editor && (
        <BottomSheet
          open={railOpen}
          onClose={dismissSheet}
          title={sheetTitle}
          className="zym-sheet-editor"
          clearTopBar
        >
          <Editor key={editorKey} patient={patient} appointmentId={appointmentId} />
        </BottomSheet>
      )}

      {ready && (
        <MobileApptSheet
          open={apptSheetOpen}
          onClose={() => setApptSheetOpen(false)}
          appointment={appointment}
          patient={patient}
          reasonName={reasonName}
          updating={updating}
          onChangeStatus={onChangeStatus}
          onReschedule={() => {
            setApptSheetOpen(false);
            onReschedule();
          }}
          rescheduleDisabledReason={rescheduleDisabledReason}
          onUpdateBookingFor={onUpdateBookingFor}
          bookingForBlockedReason={bookingForBlockedReason}
          onOpenCallNote={() => {
            setApptSheetOpen(false);
            setCallNoteOpen(true);
          }}
          pastAppointments={pastAppointments}
          upcomingAppointments={upcomingAppointments}
          touchpointsState={touchpointsState}
          onOpenAppointment={(id) => {
            setApptSheetOpen(false);
            onOpenAppointment(id);
          }}
          onOpenAppointmentDetails={onOpenAppointmentDetails}
          canPrescribe={canPrescribe}
          onCreatePrescription={onCreatePrescription}
          onCreateHealthPlan={onCreateHealthPlan}
          call={call}
        />
      )}

      {/* Portalled overlays the parent would otherwise have rendered below its
          desktop tree. addressBookUI is included because desktop mounts it
          inside PatientHeaderCard, which this screen does not render — without
          it the call icon's address book would open nothing. */}
      {ready && detailsOpen && (
        <PatientDetailsDialog
          patient={patient}
          onClose={() => setDetailsOpen(false)}
          onSaved={(updated) => {
            setDetailsOpen(false);
            if (onPatientUpdated) onPatientUpdated(updated);
          }}
        />
      )}

      {/* TWO WAYS IN, and they carry different information — desktop's rule,
          copied rather than reinvented (PatientHeaderCard.js:625-636):
            · the Call note button -> no status is known, so none is shown;
            · a status change that just SUCCEEDED -> opens by itself, naming the
              status the operator set.
          The appointment's own stored status is deliberately NOT used for the
          button path: it is a booking state, not a call outcome. */}
      {ready && (callNoteOpen || callNoteForStatus != null) && (
        <CallNoteModal
          variant={
            callNoteForStatus != null ? callNoteVariant(callNoteForStatus) : undefined
          }
          statusLabel={
            callNoteForStatus != null ? STATUS_LABEL[callNoteForStatus] : undefined
          }
          patient={patient}
          appointment={appointment}
          onClose={() => {
            setCallNoteOpen(false);
            if (onCloseCallNote) onCloseCallNote();
          }}
        />
      )}

      {/* transcriptUI removed — the AI-transcript modal is mounted
          globally by ActiveCallProvider (app root) so it survives route
          changes + minimise. Closing the dialog no longer ends the call. */}
      {ready && call.dialogsUI}
      {ready && call.addressBookUI}
      {ready && rescheduleUI}
      {dialogs}
    </div>
  );
};

MobilePatientScreen.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  ready: PropTypes.any,
  patient: PropTypes.object,
  appointment: PropTypes.object,
  appointmentId: PropTypes.number,
  languages: PropTypes.array,
  languagesState: PropTypes.string,
  onLanguagesSaved: PropTypes.func,
  condition: PropTypes.string,
  therapyTag: PropTypes.string,
  onTherapyChanged: PropTypes.func.isRequired,
  onPatientUpdated: PropTypes.func,
  reasonName: PropTypes.string,
  pastAppointments: PropTypes.array,
  upcomingAppointments: PropTypes.array,
  touchpointsState: PropTypes.string,
  onOpenAppointment: PropTypes.func.isRequired,
  onOpenAppointmentDetails: PropTypes.func.isRequired,
  /** Set by a status change that just succeeded; opens the call note. */
  callNoteForStatus: PropTypes.number,
  onCloseCallNote: PropTypes.func,
  /** Scrolled past the header's collapse threshold — folds row 3 away. */
  stuck: PropTypes.bool,
  originalBookingName: PropTypes.string,
  /** The parent's section state, so cross-section links move this rail too. */
  section: PropTypes.string,
  onSelectSection: PropTypes.func.isRequired,
  updating: PropTypes.bool,
  onChangeStatus: PropTypes.func.isRequired,
  onReschedule: PropTypes.func,
  rescheduleDisabledReason: PropTypes.string,
  /** "Update booking for" — (member) => Promise. Omit and the sheet offers none. */
  onUpdateBookingFor: PropTypes.func,
  /** When set, the control is held and this says why. */
  bookingForBlockedReason: PropTypes.string,
  canPrescribe: PropTypes.bool,
  onCreatePrescription: PropTypes.func,
  onCreateHealthPlan: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  call: PropTypes.object,
  rescheduleUI: PropTypes.node,
  dialogs: PropTypes.node,
  /** location.search, for the `?section=` deep link Create Rx uses. */
  locationSearch: PropTypes.string,
  /** Section key whose add form opens once on arrival (`&add=1`), or null. */
  autoOpenAdd: PropTypes.string,
  onAutoOpenAdd: PropTypes.func,
};

export default MobilePatientScreen;
